<script lang="ts">
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import MemberScatter, { type MemberPoint } from '../lib/charts/MemberScatter.svelte';
  import CohortSection from '../lib/charts/CohortSection.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import ContributorsEngagement from '../lib/ui/ContributorsEngagement.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    type AdminUser,
  } from '../lib/admin';
  import { withCommunity, onCommunityChange } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { CONTRIBUTOR_BLOCKS, fetchInsights, lastFor, type InsightsWith } from '@api';

  // ONE request, and it is the point of the page (doc 04 §5.2). `census` +
  // `census_spark` are the nine numbers the Overview also reads; `scatter`,
  // `cohorts` and `role_volume` are the heavy blocks only THIS page draws, so this
  // is the one place they are asked for. The dead `role-flow` fetch that used to
  // fire on every community change and render nowhere is gone with the route
  // (doc 04 §3.3 row 19, §5.6).
  type Data = InsightsWith<(typeof CONTRIBUTOR_BLOCKS)[number]>;

  let me = $state<AdminUser | null>(null);
  let data = $state<Data | null>(null);
  let loading = $state(true);
  let dataLoading = $state(false);
  let error = $state<string | null>(null);

  // Time window for the sparkline look-back (applied server-side). Default 30d.
  // Only `census_spark` moves with it — the census, the scatter and the role volume
  // classify over the fixed 30-day contributor window whatever this says.
  let days = $state(90);

  // What is already on screen, so neither the bootstrap's arrival nor a period
  // re-render can fire a second identical request.
  let prevCommunity: number | null = null;

  const keyOf = (id: number, d: number): string => `${id}:${d}`;

  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    try {
      // The page's own fetch goes out in the SAME round trip as `/v1/me`, against
      // the id already in localStorage (doc 04 §4.1 design point 2). It used to be
      // gated behind `/admin/me` → `/admin/communities`: two round trips spent
      // validating an id the browser already had.
      const d = days;
      const [boot, insights] = await Promise.all([
        ensureBootstrap(),
        withCommunity((skoolId) => {
          prevCommunity = skoolId;
          watch.seen(keyOf(skoolId, d));
          return fetchInsights(skoolId, CONTRIBUTOR_BLOCKS, { last: lastFor(d) });
        }),
      ]);
      me = asAdminUser(boot);
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      data = insights;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }

  // Reload when the community selection OR the time window changes. A real
  // community switch also clears the member search.
  const watch = onCommunityChange(
    (id) => {
      // A real community switch also clears the member search; a window change does not.
      if (id !== prevCommunity) {
        prevCommunity = id;
        selected = [];
        memberQuery = '';
      }
      void load(id, days);
    },
    {
      gate: () => !loading,
      key: (id) => {
        const d = days;
        return id == null ? null : keyOf(id, d);
      },
    },
  );

  async function load(id: number, d: number): Promise<void> {
    dataLoading = true;
    error = null;
    try {
      data = await fetchInsights(id, CONTRIBUTOR_BLOCKS, { last: lastFor(d) });
    } catch (err: unknown) {
      // `loadedKey` deliberately KEEPS what failed: the effect above reads it, so
      // clearing it here would re-enter `load()` the instant `error` is assigned —
      // a tight retry loop against an endpoint that is already failing. The retry
      // is the error block's button, which re-runs `init`.
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load contributor insights.';
      data = null;
    } finally {
      dataLoading = false;
    }
  }

  // The scatter's author card is NESTED and may be null (§2.1: an unresolvable
  // author is null, not a fake member) — flattened here into the chart's own point
  // type, which `Landing.svelte` also feeds from a different source. A dot we
  // cannot name is dropped rather than plotted as "Unknown".
  const allPoints = $derived<MemberPoint[]>(
    (data?.scatter.points ?? []).flatMap((p) =>
      p.member == null
        ? []
        : [{
            member_id: p.member.member_id,
            name: p.member.name,
            handle: p.member.handle,
            picture_profile: p.member.picture_profile,
            activity: p.activity,
            value_per_contribution: p.value_per_contribution,
            upvotes_received: p.upvotes_received,
            quadrant: p.quadrant,
            stage: p.stage,
          }],
    ),
  );

  // Member search → isolate one or more members on the plot. Selected members
  // persist across window changes; only those active in the window can plot.
  let memberQuery = $state('');
  let selected = $state<{ member_id: number; name: string }[]>([]);
  const selectedIds = $derived(new Set(selected.map((s) => s.member_id)));

  const suggestions = $derived.by(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return [];
    return allPoints
      .filter((p) => !selectedIds.has(p.member_id) &&
        (p.name.toLowerCase().includes(q) || (p.handle ?? '').toLowerCase().includes(q)))
      .slice(0, 8);
  });

  const scatterPoints = $derived(
    selected.length ? allPoints.filter((p) => selectedIds.has(p.member_id)) : allPoints,
  );
  // Selected members with no activity in the current window (nothing to plot).
  const missingSelected = $derived(selected.length ? selected.length - scatterPoints.length : 0);

  function addMember(p: MemberPoint): void {
    if (!selectedIds.has(p.member_id)) selected = [...selected, { member_id: p.member_id, name: p.name }];
    memberQuery = '';
  }
  function removeMember(id: number): void {
    selected = selected.filter((s) => s.member_id !== id);
  }

  function pickMember(p: { member_id: number }): void {
    navigate(`/admin/community/members/${p.member_id}`);
  }

  void init();
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error && !data}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <PageHeader
        section="Insights"
        title="Contributors"
        updated={data?.last_updated}
        scope="Contributor classification uses a fixed 30-day window"
      />

      {#if dataLoading}
        <StateBlock state="loading" />
      {:else if data}
        <!-- Contributors & Engagement — role census + momentum sparklines; the period sets the look-back -->
        <ContributorsEngagement
          bind:days
          census={data.census}
          spark={data.census_spark}
          roleVolume={data.role_volume}
          class="mt-5"
        />

        <!-- Value vs volume map · dots coloured by momentum -->
        <section class="mt-6 rounded-box border hair bg-base-100 p-5">
          <div class="eyebrow mb-1">Contributors map <span class="text-base-content/58">· last 30 days</span></div>
          <p class="mb-3 text-xs text-base-content/65">
            Each member (excluding moderators) placed by how <em>much</em> they post (volume, →)
            vs. how many upvotes each post earns (value, ↑) over the last <strong>30 days</strong> — the
            same window as their role everywhere else, so it doesn't shift with the period above. The
            <strong>tinted regions</strong> are the four value/volume quadrants (split at the community
            medians); each <strong>dot's colour</strong> is the member's momentum. So a key voice in the
            top-right glowing <span class="text-error">red</span> is a valuable member slipping. Scroll to
            zoom, drag to pan, click a face to open the member.
          </p>

          <!-- member search -->
          <div class="relative mb-3">
            <input
              type="search"
              placeholder="Find members… (isolate one or more on the plot)"
              class="input input-bordered input-sm w-full bg-base-100 font-normal"
              bind:value={memberQuery}
              aria-label="Search members"
            />
            {#if suggestions.length > 0}
              <ul class="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-box border hair bg-base-100 py-1 shadow-xl">
                {#each suggestions as s (s.member_id)}
                  <li>
                    <button class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-base-200/60" onclick={() => addMember(s)}>
                      <Avatar src={s.picture_profile ?? null} name={s.name} size={22} memberId={s.member_id} />
                      <span class="redact min-w-0 flex-1 truncate">{s.name}</span>
                      <span class="redact font-mono text-[0.65rem] text-base-content/62">{s.activity} · ▲{s.upvotes_received}</span>
                    </button>
                  </li>
                {/each}
              </ul>
            {/if}
            {#if selected.length > 0}
              <div class="mt-2 flex flex-wrap items-center gap-1.5">
                {#each selected as s (s.member_id)}
                  <span class="inline-flex items-center gap-1 rounded bg-primary/10 py-0.5 pl-2 pr-1 text-xs text-primary">
                    <span class="redact">{s.name}</span>
                    <button class="rounded px-0.5 hover:bg-primary/20" onclick={() => removeMember(s.member_id)} aria-label="Remove {s.name}">✕</button>
                  </span>
                {/each}
                <button class="text-xs text-base-content/65 hover:text-primary hover:underline" onclick={() => (selected = [])}>clear</button>
              </div>
            {/if}
          </div>

          {#if scatterPoints.length === 0}
            <p class="text-sm text-base-content/62">
              {#if selected.length > 0}
                {selected.length === 1 ? 'That member has' : 'Those members have'} no activity in the last 30 days.
              {:else}
                No contributor activity in the last 30 days.
              {/if}
            </p>
          {:else}
            <MemberScatter
              points={scatterPoints}
              xMedian={data.scatter.median_activity}
              yMedian={data.scatter.median_value}
              total={scatterPoints.length}
              colorBy="stage"
              quadrantFills
              onpick={pickMember}
            />
            {#if missingSelected > 0}
              <p class="mt-1 font-mono text-[0.6rem] text-base-content/58">
                {missingSelected} selected member{missingSelected === 1 ? '' : 's'} had no activity in the last 30 days
              </p>
            {/if}
          {/if}
        </section>

        <!-- First-contribution cohorts · do new contributors keep contributing -->
        <CohortSection cohorts={data.cohorts} class="mt-6" />

      {/if}
    </PageContainer>
  {/if}
</AdminShell>
